import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { InputNumber } from 'antd';
import { cn } from '../../utils';
import { CustomRegisterOptions } from '../../types';

interface NumberInputProps<T extends FieldValues> {
  name: Path<T>;
  control?: UseFormReturn<T>['control'];
  form?: UseFormReturn<T>;
  rules?: CustomRegisterOptions<T>;
  label?: string | React.ReactNode;
  formState?: UseFormReturn<T>['formState'];
  addOnClass?: string;
  config?: Record<string, any>;
}

/**
 * A reusable component for rendering a number input field with validation and error handling.
 * @template T - The type of the form values.
 * @param {NumberInputProps<T>} props - The properties for the NumberInput component.
 * @param {Path<T>} props.name - The name of the number input field.
 * @param {UseFormReturn<T>['control']} [props.control] - The control object from react-hook-form.
 * @param {CustomRegisterOptions<T>} [props.rules] - The validation rules for the input field.
 * @param {string | React.ReactNode} [props.label] - The label for the input field.
 * @param {UseFormReturn<T>['formState']} [props.formState] - The form state from react-hook-form.
 * @param {string} addOnClass - Additional Classes for the input field
 * @param {Record<string, any>} [props.config={}] - Additional configuration for the input field.
 * @returns {JSX.Element} - The rendered NumberInput component.
 */
const NumberInput = <T extends FieldValues>({
  name,
  control,
  form,
  rules,
  label,
  formState,
  addOnClass = '',
  config = {},
}: NumberInputProps<T>) => {
  const effectiveControl = control || form?.control;
  const effectiveFormState = formState || form?.formState;
  const errors = (effectiveFormState?.errors || {}) as Record<string, any>;

  if (!name || !effectiveControl) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-xs font-medium text-text-100 whitespace-nowrap">
          {label}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <InputNumber
            type="number"
            className={addOnClass}
            {...field}
            {...config}
            onWheel={e => {
              (e.target as HTMLElement).blur();
            }}
          />
        )}
      />
      <div
        className={cn(
          'text-xs text-red-500',
          { visible: errors[name] },
          { invisible: !errors[name] },
        )}
      >
        {errors[name] && `${errors[name]?.message}`}
      </div>
    </div>
  );
};

export default NumberInput;
